import { UserDocumentsViewModelPort } from 'documents/views/UserDocumentsViewModelAdapter'
import { task } from 'mobx-task'
import { computed, observable, action, makeObservable } from 'mobx'
import { orderBy } from 'lodash'
import Document from 'documents/Document'
import Workspace from 'iam/models/Workspace'
import { TableSortOrder } from 'components/Table'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import { AlertState } from 'alerts/Alert'

export type DocsTableColumn = {
  name:
    | 'name'
    | 'workspace'
    | 'documentType'
    | 'dateModified'
    | 'actions'
    | 'fileType'
  header: string
  key:
    | keyof Pick<
        Document,
        'title' | 'dateCreated' | 'fileType' | 'dateModified'
      >
    | 'actions'
    | 'workspace.id'
    | 'documentType.name'
  sortable?: boolean
}
export const docsTableColumns: DocsTableColumn[] = [
  {
    name: 'name',
    header: 'Name',
    key: 'title',
    sortable: true,
  },
  {
    name: 'workspace',
    header: 'Workspace',
    key: 'workspace.id',
    sortable: true,
  },
  {
    name: 'documentType',
    header: 'Document Type',
    key: 'documentType.name',
  },
  {
    name: 'fileType',
    header: 'Type',
    key: 'fileType',
    sortable: true,
  },
  {
    name: 'dateModified',
    header: 'Last Updated',
    key: 'dateModified',
    sortable: true,
  },
]

export default class DocumentsListViewModel {
  @observable searchText = ''
  @observable sortOrder: TableSortOrder = 'desc'
  @observable sortField: DocsTableColumn['key'] = 'dateModified'

  confirmDocumentDelete = new ConfirmViewModel()

  constructor(
    private adapter: UserDocumentsViewModelPort,
    private getDocumentsWorkspaceForFiltering: () => Workspace | undefined,
  ) {
    makeObservable(this)
  }

  @computed
  get columns() {
    if (!this.selectedWorkspace) {
      return docsTableColumns
    }

    return docsTableColumns.filter((c) => c.name !== 'workspace')
  }

  isColumnHeaderVisible(colName: DocsTableColumn['name']) {
    return this.columns.map((col) => col.name).includes(colName)
  }

  @computed
  get documents() {
    return this.adapter.documents
  }

  @computed
  get selectedWorkspace() {
    return this.getDocumentsWorkspaceForFiltering()
  }

  @computed
  get workspaceDocs() {
    const workspace = this.selectedWorkspace
    if (!workspace) {
      return this.documents
    }

    return this.documents.filter((doc) => doc.workspaceId === workspace.id)
  }

  @computed
  get filteredDocuments() {
    let filtered = this.workspaceDocs

    if (this.searchText !== '') {
      filtered = filtered.filter((doc) =>
        doc.title.toLowerCase().includes(this.searchText.toLowerCase()),
      )
    }

    return orderBy(
      filtered,
      [this.sortField],
      this.sortOrder === 'none' ? undefined : this.sortOrder,
    )
  }

  @computed
  get currentSort() {
    return this.sortField
  }

  @computed
  get currentSortOrder() {
    return this.sortOrder
  }

  @action.bound
  setSort(sortField: DocsTableColumn['key'], sortOrder: TableSortOrder) {
    this.sortField = sortField
    this.sortOrder = sortOrder
  }

  @action.bound
  setSearchText(query: string) {
    this.searchText = query
  }

  download = (document: Document) => {
    return this.adapter.documentActionsAdapter.downloadDocument(document)
  }

  delete = task.resolved(async (document: Document) => {
    if (!(await this.confirmDocumentDelete.show())) {
      return
    }

    try {
      const docTitle = document.title
      await this.adapter.documentActionsAdapter.archiveDocument(document)

      this.adapter
        .newAlert()
        .withState(AlertState.Success)
        .saying(`Success!  Document ${docTitle} has been removed!`)
        .autoDismiss()
        .show()
    } catch (error) {
      this.adapter.newAlert().fromError(error).show()
      console.error(error)
    }
  })
}
